import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { AxiosError } from 'axios';
import { PasswordResetState } from './passwordResetTypes';
import * as VseAlert from '../../features/alert';
import { useTypedSelector } from '../../store';

const msg404 = 'The password reset link is invalid or has been expired';

const initialState = {
  loading: 'idle',
} as PasswordResetState;

export const forgotPassword = createAsyncThunk('password/forgotPassword', async (userName: string, thunkApi) => {
  let data = new FormData();
  data.append('userName', userName);
  try {
    const response = await axios.post('api/identity/forgotPassword', data);
    return response;
  } catch (err) {
    const error: AxiosError = err;
    VseAlert.handleThunkError(thunkApi, error, msg404);
    return thunkApi.rejectWithValue(null);
  }
});

export const verify = createAsyncThunk('password/verify', async ({ id, token }: {
  id: string,
  token: string,
}, thunkApi) => {
  let data = new URLSearchParams();
  data.append('id', id);
  data.append('token', token);
  try {
    const response = await axios.get(`api/identity/resetPassword?${data.toString()}`);
    return response;
  } catch (err) {
    const error: AxiosError = err;
    VseAlert.handleThunkError(thunkApi, error, msg404);
    return thunkApi.rejectWithValue(null);
  }
});

export const resetPassword = createAsyncThunk('password/resetPassword', async ({ id, token, homeUniversityID, password, confirmPassword }: {
  id: string,
  token: string,
  homeUniversityID: number,
  password: string,
  confirmPassword: string,
}, thunkApi) => {

  let query = new URLSearchParams();
  query.append('id', id);
  query.append('token', token);

  let data: {
    homeUniversityID?: number,
    password?: string,
    confirmPassword?: string,
  } = {};

  if (homeUniversityID) data.homeUniversityID = homeUniversityID;
  if (password) data.password = password;
  if (confirmPassword) data.confirmPassword = confirmPassword;

  try {
    const response = await axios.post(`api/identity/resetPassword?${query.toString()}`, data);
    return response;
  } catch (err) {
    const error: AxiosError = err;
    VseAlert.handleThunkError(thunkApi, error);
    return thunkApi.rejectWithValue(null);
  }
});

export const passwordResetSlice = createSlice({
  name: 'passwordReset',
  initialState,
  reducers: {
    reset: (state) => {
      state = Object.assign(state, initialState);
    },
    dismissBadRequestError: (state) => {
      state.validationProblemDetails = undefined;
    },
  },
  extraReducers: builder => {
    builder.addCase(forgotPassword.pending, (state) => {
      state.loading = 'loading';
    });
    builder.addCase(forgotPassword.fulfilled, (state) => {
      state.loading = 'succeeded';
    });
    builder.addCase(forgotPassword.rejected, (state) => {
      state.loading = 'failed';
    });
    builder.addCase(verify.pending, (state) => {
      state.loading = 'loading';
    });
    builder.addCase(verify.fulfilled, (state) => {
      state.loading = 'pending';
    });
    builder.addCase(verify.rejected, (state) => {
      state.loading = 'failed';
    });
    builder.addCase(resetPassword.pending, (state) => {
      state.loading = 'loading';
    });
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.loading = 'succeeded';
    });
    builder.addCase(resetPassword.rejected, (state) => {
      state.loading = 'pending';
    });
  },
});

export const {
  reset,
} = passwordResetSlice.actions;

export default passwordResetSlice.reducer;

export const usePasswordResetState = (): PasswordResetState => useTypedSelector(state => state.passwordReset);

